'use client';

import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { TbBrandLinkedin, TbBrandYoutube } from 'react-icons/tb';

import { Anchor, Box, Flex, Text } from '@mantine/core';

import { MarketingHelpers } from 'helpers/MarketingHelpers';
import { RouterHelpers } from 'helpers/RouterHelpers';

import { useHeaderState } from 'components/header/hooks/use-header-state';
import { DEPVertical } from 'components/logo/DEPVertical';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { FeedbackService } from 'services/FeedbackService';
import type { WorkspaceConfigState } from 'state/workspace-config';

import { Container } from '../../../marketing/components/container/Container';

import cx from './Footer.module.scss';

type FooterLink = {
    label: string;
    link?: string;
    onClick?: (event: React.MouseEvent) => void;
    icon?: React.ComponentType;
};

type FooterItem = {
    title: string;
    links: FooterLink[];
};

const COMPANY_ITEM: FooterItem = {
    title: 'Company',
    links: [
        { label: 'About us', link: MarketingHelpers.urls.about() },
        { label: 'News', link: MarketingHelpers.urls.news() },
        {
            label: 'Follow us on LinkedIn',
            link: 'https://www.linkedin.com/company/direct-energy-partners',
            icon: TbBrandLinkedin,
        },
        {
            label: 'Watch us on YouTube',
            link: 'https://www.youtube.com/@dcmicrogriddesignandengine5189',
            icon: TbBrandYoutube,
        },
        { label: 'Feedback', onClick: () => FeedbackService.open() },
        { label: 'Contact', link: MarketingHelpers.urls.dcide.contact() },
    ],
};
const RESOURCE_PRO_ITEM: FooterItem = {
    title: 'RE+Source PRO',
    links: [
        { label: 'About', link: MarketingHelpers.urls.resourcepro.overview() },
        { label: 'Join as a PRO', link: MarketingHelpers.urls.resourcepro.joinAsPro() },
        { label: 'Find Services', link: RouterHelpers.urls.profiles.overview() },
        { label: 'Find Products', link: RouterHelpers.urls.products.overview() },
        { label: 'FAQs', link: MarketingHelpers.urls.resourcepro.faq() },
    ],
};

const DEFAULT_ITEMS: FooterItem[] = [
    COMPANY_ITEM,
    {
        title: 'Design Software',
        links: [
            { label: 'About', link: MarketingHelpers.urls.dcide.overview() },
            { label: 'Blog', link: MarketingHelpers.urls.blog() },
            { label: 'Updates', link: MarketingHelpers.urls.updates() },
            { label: 'Tutorials', link: MarketingHelpers.urls.tutorials() },
            { label: 'Pricing', link: RouterHelpers.urls.projects.upgrade() },
            { label: 'Contact', link: MarketingHelpers.urls.dcide.contact() },
        ],
    },
    {
        title: 'Microgrid Controller',
        links: [
            { label: 'About', link: MarketingHelpers.urls.controller.overview() },
            { label: 'Documentation', link: 'https://docs.directenergypartners.com' },
            { label: 'Price Configurator', link: MarketingHelpers.urls.controller.pricingConfigurator() },
            { label: 'Pricing', link: MarketingHelpers.urls.controller.pricing() },
            { label: 'Contact', link: MarketingHelpers.urls.controller.contact() },
            { label: 'FAQ', link: MarketingHelpers.urls.controller.faq() },
        ],
    },
    RESOURCE_PRO_ITEM,
];

const RESOURCE_PRO_ITEMS = [RESOURCE_PRO_ITEM];

const getWorkspaceItems = ({ showContactLink, showFeedbackLink }: WorkspaceConfigState): FooterItem[] => {
    const links: FooterLink[] = [
        ...(showContactLink ? [{ label: 'Contact', link: MarketingHelpers.urls.dcide.contact() }] : []),
        ...(showFeedbackLink ? [{ label: 'Feedback', onClick: () => FeedbackService.open() }] : []),
    ];

    return links.length > 0 ? [{ title: 'Support', links }] : [];
};

const FooterAnchor = ({ link, label, onClick, icon: Icon }: FooterLink) => {
    if (onClick) {
        return (
            <Anchor className={cx.link} onClick={onClick} component="button" type="button">
                {Icon && <Icon />}
                {label}
            </Anchor>
        );
    }

    return (
        <Anchor
            component={Link}
            className={cx.link}
            href={link ?? '#'}
            target={link?.startsWith('http') ? '_blank' : '_self'}
        >
            {Icon && <Icon />}
            {label}
        </Anchor>
    );
};

const Footer = () => {
    const { showResourceProNav } = useHeaderState();

    const workspaceConfig = useWorkspaceConfig();

    const data = workspaceConfig.workspace
        ? getWorkspaceItems(workspaceConfig)
        : showResourceProNav
          ? RESOURCE_PRO_ITEMS
          : DEFAULT_ITEMS;

    return (
        <footer className={cx.footer}>
            <Container padding="x">
                <Flex className={cx.columns}>
                    <div className={cx.logo}>
                        {workspaceConfig.logoWhiteUrl ? (
                            <img
                                src={workspaceConfig.logoWhiteUrl}
                                alt="Logo"
                                height={40}
                                className={cx.workspaceLogo}
                            />
                        ) : (
                            <Anchor href="/">
                                <DEPVertical c="white" />
                            </Anchor>
                        )}
                    </div>
                    {data.map((group) => (
                        <div className={cx.wrapper} key={group.title}>
                            <Text className={cx.title}>{group.title}</Text>
                            <Box className={cx.links}>
                                {group.links.map((item, index) => (
                                    <FooterAnchor key={index} {...item} />
                                ))}
                            </Box>
                        </div>
                    ))}
                </Flex>
            </Container>
            <Container padding="x">
                <Flex className={cx.closure}>
                    <Box className={cx.closureLinks}>
                        {[
                            { label: 'License Agreement', url: MarketingHelpers.urls.licenseAgreement() },
                            { label: 'Privacy Policy', url: '/dcide-privacy-policy.pdf' },
                            { label: 'Data Processing', url: '/dcide-data-processing.pdf' },
                        ].map((link) => (
                            <Anchor className={cx.link} component={Link} href={link.url} key={link.url}>
                                {link.label}
                            </Anchor>
                        ))}
                    </Box>
                    <Box className={cx.closureDisclaimer}>
                        © {new Date().getFullYear()} Direct Energy Partners. All rights reserved.
                    </Box>
                </Flex>
            </Container>
        </footer>
    );
};

export { Footer };
